import { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { circleService, type SkillChannel } from '../services/api'

const CHANNELS: Array<{ id: SkillChannel | ''; label: string }> = [
  { id: '', label: 'Aucun canal' },
  { id: 'circle', label: 'Cercle (Core)' },
  { id: 'mail', label: 'Courriel (SMTP)' },
  { id: 'discord', label: 'Discord' },
  { id: 'slack', label: 'Slack' },
  { id: 'whatsapp', label: 'WhatsApp' },
]

function errorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const data = (error as { response?: { data?: { error?: { message?: string }; message?: string } } }).response?.data
    return data?.error?.message || data?.message || 'La requête a échoué'
  }
  return error instanceof Error ? error.message : 'La requête a échoué'
}

/**
 * * Admin editor for the formal skill catalog.
 */
export function SkillCatalogPanel() {
  const queryClient = useQueryClient()
  const catalog = useQuery('skill-catalog', () => circleService.catalog())
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [channel, setChannel] = useState<SkillChannel | ''>('')
  const [editing, setEditing] = useState<string | null>(null)
  const [notice, setNotice] = useState('')

  const reset = () => {
    setName('')
    setDescription('')
    setChannel('')
    setEditing(null)
  }

  const save = async () => {
    setNotice('')
    try {
      if (editing) {
        await circleService.updateCatalogSkill(editing, { name, description, channel })
      } else {
        await circleService.createCatalogSkill({ name, description, channel })
      }
      reset()
      await queryClient.invalidateQueries('skill-catalog')
    } catch (error) {
      setNotice(errorMessage(error))
    }
  }

  return (
    <div className="card">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-primary">Catalogue de compétences</h2>
        <p className="text-sm text-secondary mt-1">
          Ces compétences se choisissent dans les rôles. Une compétence écrite à la volée n'entre pas ici.
        </p>
      </div>
      <div className="px-6 py-4 space-y-4">
        {notice ? <p className="text-sm text-error">{notice}</p> : null}
        <ul className="space-y-2">
          {(catalog.data ?? []).map((skill) => (
            <li key={skill.id} className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-primary">{skill.name}</p>
                <p className="text-xs text-secondary">{skill.description}</p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="button button--secondary"
                  onClick={() => {
                    setEditing(skill.id)
                    setName(skill.name)
                    setDescription(skill.description)
                    setChannel(skill.channel ?? '')
                  }}
                >
                  Modifier
                </button>
                <button
                  type="button"
                  className="button button--secondary"
                  onClick={() => {
                    if (!confirm(`Retirer « ${skill.name} » du catalogue ?`)) {
                      return
                    }
                    void circleService.deleteCatalogSkill(skill.id).then(() => queryClient.invalidateQueries('skill-catalog')).catch((error: unknown) => setNotice(errorMessage(error)))
                  }}
                >
                  Retirer
                </button>
              </div>
            </li>
          ))}
        </ul>
        <div className="grid gap-2">
          <label className="text-sm text-secondary" htmlFor="catalog-skill-name">
            Nom
            <input id="catalog-skill-name" name="catalog-skill-name" className="form-input mt-1" value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <label className="text-sm text-secondary" htmlFor="catalog-skill-description">
            Description
            <textarea id="catalog-skill-description" name="catalog-skill-description" className="form-input mt-1" rows={2} value={description} onChange={(event) => setDescription(event.target.value)} />
          </label>
          <label className="text-sm text-secondary" htmlFor="catalog-skill-channel">
            Canal
            <select id="catalog-skill-channel" name="catalog-skill-channel" className="form-input mt-1" value={channel} onChange={(event) => setChannel(event.target.value as SkillChannel | '')}>
              {CHANNELS.map((item) => (
                <option key={item.id || 'none'} value={item.id}>{item.label}</option>
              ))}
            </select>
          </label>
          <div className="flex gap-2">
            <button type="button" className="button button--primary" onClick={() => void save()} disabled={!name.trim()}>
              {editing ? 'Enregistrer' : 'Ajouter au catalogue'}
            </button>
            {editing ? (
              <button type="button" className="button button--secondary" onClick={reset}>Annuler</button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
